// src/components/energy-reset/ArtifactModal.tsx
import { useEffect, useState } from "react";
import Modal from "../Modal";
import type { Artifact } from "../../api/artifacts";

type Variant = "master" | "story" | "feed" | "twitter";
type Mode = "award" | "preview" | "locked";
type LockedReason = "future" | "today" | "missed";

interface Props {
  open: boolean;
  day: number | null;     // какой день показываем
  total: number;          // общая длительность курса (для текста "День X/N")
  onClose: () => void;    // закрытие модалки (в award — это «Далее»)
  mode?: Mode;            // 'award' | 'preview' | 'locked' (по умолчанию preview)
  lockedReason?: LockedReason; // причина блокировки для 'locked'
  artifact?: Artifact | null;
}

function pickDefaultVariant(rec: Artifact | null): Variant {
  if (rec?.image_master) return "master";
  if (rec?.image_story) return "story";
  if (rec?.image_feed) return "feed";
  return "twitter";
}

export default function ArtifactModal({
  open,
  day,
  total,
  onClose,
  mode = "preview",
  lockedReason,
  artifact,
}: Props) {
  const data = artifact ?? null;

  const [variant, setVariant] = useState<Variant>("master");
  const [shareOpen, setShareOpen] = useState(false);

  // синхронизируем выбранный вариант при смене дня/набора картинок
  useEffect(() => {
    setVariant(pickDefaultVariant(data));
  }, [day, artifact?.image_master?.url]);

  if (!open) return null;

  const isAward = mode === "award";
  const isLocked = mode === "locked";
  const isPreview = mode === "preview";

  const titleFromData = data?.title ?? "Артефакт";
  const heading = isLocked
    ? "Артефакт закрыт"
    : isAward
    ? "Новый артефакт!"
    : titleFromData;

  const subtitle =
    day ? (isAward ? `День ${day}/${total} • получено` : `День ${day}/${total}`) : "";

  const getUrl = (v: Variant): string => {
    const img = (data as any)?.[`image_${v}`];
    return (
      img?.url ||
      data?.image_master?.url ||
      data?.image_story?.url ||
      data?.image_feed?.url ||
      data?.image_twitter?.url ||
      ""
    );
  };

  const imgUrl = getUrl(variant);

  const has = {
    story: !!data?.image_story,
    feed: !!data?.image_feed,
    twitter: !!data?.image_twitter,
  };

  const shareText = data?.share_text ?? `${subtitle} — ${titleFromData}`;

  const lockedHint =
    lockedReason === "future"
      ? "Этот артефакт станет доступен позже."
      : lockedReason === "today"
      ? "Заверши практику сегодня, чтобы открыть артефакт."
      : lockedReason === "missed"
      ? "День пропущен — артефакт недоступен."
      : "Артефакт недоступен.";

  async function shareFileIfPossible(url: string, filename: string, text: string) {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      const file = new File([blob], filename, { type: blob.type || "image/png" });

      // @ts-ignore — типизация canShare в TS иногда капризничает
      if (navigator.canShare && (navigator as any).canShare({ files: [file] })) {
        await (navigator as any).share({ files: [file], title: titleFromData, text });
        return true;
      }
    } catch {
      // ignore — пойдём во фолбэк
    }
    return false;
  }

  // Копируем текст СНАЧАЛА, затем открываем новую вкладку. Обязательно ловим ошибки.
  async function openInNewTabAndCopyText(url: string, text: string) {
    if (document.hasFocus() && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text).catch(() => {});
    } else {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try {
          document.execCommand("copy");
        } catch {}
        document.body.removeChild(ta);
      } catch {}
    }
    if (url) window.open(url, "_blank");
  }

  // --- обработчики пунктов меню «Поделиться» ---
  const shareUniversal = async () => {
    const v: Variant = "master";
    setVariant(v);
    setShareOpen(false);
    const url = getUrl(v);
    const filename = data?.image_master?.filename ?? `artifact_day${day ?? ""}.png`;
    const ok = await shareFileIfPossible(url, filename, shareText);
    if (!ok) await openInNewTabAndCopyText(url, shareText);
  };

  const shareStory = async () => {
    const v: Variant = "story";
    setVariant(v);
    setShareOpen(false);
    const url = getUrl(v);
    const filename = data?.image_story?.filename ?? `artifact_day${day ?? ""}.png`;
    const ok = await shareFileIfPossible(url, filename, shareText);
    if (!ok) await openInNewTabAndCopyText(url, shareText);
  };

  const shareFeed = async () => {
    const v: Variant = "feed";
    setVariant(v);
    setShareOpen(false);
    const url = getUrl(v);
    const filename = data?.image_feed?.filename ?? `artifact_day${day ?? ""}.png`;
    const ok = await shareFileIfPossible(url, filename, shareText);
    if (!ok) await openInNewTabAndCopyText(url, shareText);
  };

  const shareTwitter = () => {
    const v: Variant = "twitter";
    setVariant(v);
    setShareOpen(false);
    const url = encodeURIComponent(getUrl(v));
    const text = encodeURIComponent(shareText);
    const intent = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    window.open(intent, "_blank");
  };

  const handleSave = () => {
    if (!imgUrl || isLocked) return;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = (data as any)?.[`image_${variant}`]?.filename ?? `artifact_day${day ?? ""}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      placement="center"
      maxWidth="max-w-md"
      /* высоту контролируем внутри через max-h-[80vh] */
    >
      {/* Колонка: шапка/скролл-контент/фикс-футер */}
      <div className="flex flex-col max-h-[80vh]">
        {/* Шапка + скролл-контент */}
        <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
          <div
            className={[
              "mx-auto mb-3 h-12 w-12 rounded-full flex items-center justify-center text-white text-2xl shadow-lg",
              isLocked ? "bg-gray-400/80" : "bg-amber-400/90",
            ].join(" ")}
          >
            {isLocked ? "🔒" : "✦"}
          </div>

          <h3 className="text-xl font-bold text-center">{heading}</h3>
          <div className="text-sm text-gray-500 mt-0.5 text-center">{subtitle}</div>

          {/* Превью: ограничиваем высоту, чтобы чипы и кнопки всегда были видны */}
          {imgUrl ? (
            <div className="relative mt-4 w-full rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <img
                src={imgUrl}
                alt={titleFromData}
                className={
                  "w-full h-auto max-h-[56vh] object-contain select-none " +
                  (isLocked ? "opacity-70 grayscale blur-[2px]" : "")
                }
                draggable={false}
              />
              {isLocked && (
                <div className="absolute inset-0 grid place-items-center bg-black/35">
                  <div className="flex flex-col items-center text-white text-center gap-1 px-4">
                    <div className="text-3xl">🔒</div>
                    <div className="text-xs/5 opacity-95">{lockedHint}</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4 h-48 rounded-2xl border border-dashed grid place-items-center text-gray-400">
              Нет изображения для этого дня
            </div>
          )}

          {/* Мантра / короткий эффект — опционально (только не в locked) */}
          {!isLocked && (data?.mantra || data?.short_benefit) && (
            <div className="mt-3 space-y-1 text-center">
              {data?.mantra && <div className="text-sm font-medium">{data.mantra}</div>}
              {data?.short_benefit && (
                <div className="text-xs text-gray-500">{data.short_benefit}</div>
              )}
            </div>
          )}

          {/* Переключатель форматов — скрыт в locked */}
          {!isLocked && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <VariantChip active={variant === "master"} onClick={() => setVariant("master")}>
                Квадрат
              </VariantChip>

              {has.story && (
                <VariantChip active={variant === "story"} onClick={() => setVariant("story")}>
                  Story
                </VariantChip>
              )}

              {has.feed && (
                <VariantChip active={variant === "feed"} onClick={() => setVariant("feed")}>
                  Feed
                </VariantChip>
              )}

              {has.twitter && (
                <VariantChip active={variant === "twitter"} onClick={() => setVariant("twitter")}>
                  X/Twitter
                </VariantChip>
              )}
            </div>
          )}
        </div>

        {/* Футер с CTA — всегда виден */}
        <div className="px-5 pt-3 pb-4 border-t bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/60">
          <div className="flex items-center justify-center gap-3 flex-wrap relative">
            {/* В locked скрываем шэр/сейв */}
            {!isLocked && (
              <>
                {/* Кнопка Поделиться с мини-меню */}
                <div className="relative">
                  <button
                    onClick={() => setShareOpen((v) => !v)}
                    className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                  >
                    Поделиться
                  </button>

                  {shareOpen && (
                    <div className="absolute bottom-full left-0 z-20 mb-2 w-[220px] rounded-xl border bg-white shadow-lg p-2 text-sm">
                      <ShareItem onClick={shareStory}>Сторис в Instagram</ShareItem>
                      <ShareItem onClick={shareFeed}>Пост в Instagram</ShareItem>
                      <ShareItem onClick={shareTwitter}>Поделиться в X/Twitter</ShareItem>
                      <div className="my-1 border-t" />
                      <ShareItem onClick={shareUniversal}>Универсальный вариант</ShareItem>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleSave}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 font-semibold"
                >
                  Сохранить
                </button>
              </>
            )}

            {/* Основная кнопка: в award — «Далее», в preview — «Закрыть», в locked — «Понятно» */}
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-gray-300 font-semibold"
            >
              {isAward ? "Далее" : isPreview ? "Закрыть" : "Понятно"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function VariantChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "px-3 py-1.5 rounded-full text-xs font-semibold border transition " +
        (active
          ? "bg-gray-900 text-white border-gray-900"
          : "bg-white text-gray-700 border-gray-300 hover:border-gray-400")
      }
    >
      {children}
    </button>
  );
}

function ShareItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50">
      {children}
    </button>
  );
}
